import {
  Box,
  Flex,
  Select,
  VisuallyHidden,
  Grid,
  GridItem,
} from '@everlywell/ui-kit';
import React from 'react';

export type ResourceType = 'All' | '1 Pager' | 'Whitepaper' | 'Case Study';
export type AudienceType =
  | 'All'
  | 'Health Plans'
  | 'Employers'
  | 'Life Sciences'
  | 'Labs'
  | 'Providers'
  | 'Partners';

export type FocusType =
  | 'All'
  | 'Diabetes'
  | 'Kidney Health'
  | 'Cancer Screening'
  | 'Heart Health'
  | 'Sexual Health'
  | 'Telehealth';
export type SortType = 'Latest' | 'Oldest' | 'A-Z' | 'Z-A';

// FilterSelect component for reusability
interface FilterSelectProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: T[];
  label: string;
  placeholder: string;
  width?: string | string[];
  id?: string;
}

const FilterSelect = <T extends string>({
  value,
  onChange,
  options,
  label,
  placeholder,
  width = ['100%', '100%', '200px'],
  id,
}: FilterSelectProps<T>) => {
  const selectId = id || `filter-${label.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <Box width="100%">
      <VisuallyHidden>
        <label htmlFor={selectId}>{label}</label>
      </VisuallyHidden>
      <Select
        id={selectId}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        aria-label={label}
        width={width}
        borderColor="#F2F2ED"
        fontSize={['14px', '16px']}
        color="#545454"
        fontWeight="normal"
        height={['44px', '48px']}
        _focus={{
          borderColor: '#007377',
          boxShadow: '0 0 0 1px #007377',
        }}
      >
        <option value="All">{placeholder}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
    </Box>
  );
};

// ResourcesFilterBar component
interface ResourcesFilterBarProps {
  type: ResourceType;
  setType: (value: ResourceType) => void;
  audience: AudienceType;
  setAudience: (value: AudienceType) => void;
  focus: FocusType;
  setFocus: (value: FocusType) => void;
  sort: SortType;
  setSort: (value: SortType) => void;
  uniqueTypes: ResourceType[];
  uniqueAudiences: AudienceType[];
  uniqueFocuses: FocusType[];
}

const ResourcesFilterBar: React.FC<ResourcesFilterBarProps> = ({
  type,
  setType,
  audience,
  setAudience,
  focus,
  setFocus,
  sort,
  setSort,
  uniqueTypes,
  uniqueAudiences,
  uniqueFocuses,
}) => (
  <Flex
    flexDirection="column"
    justifyContent="space-between"
    alignItems="stretch"
    mb={[4, 5, 6]}
    gap={[3, 3, 4]}
    role="search"
    aria-label="Resource filters"
    width="100%"
  >
    {/* Mobile and tablet view: Grid layout */}
    <Grid
      display={['grid', 'grid', 'none']}
      gridTemplateColumns={['repeat(2, 1fr)', 'repeat(2, 1fr)']}
      gap={[0, 2, 3]}
      width="100%"
    >
      <GridItem>
        <FilterSelect<ResourceType>
          id="resource-type-filter"
          value={type}
          onChange={(value) => setType(value)}
          options={uniqueTypes}
          label="Filter by type"
          placeholder="Category: All"
        />
      </GridItem>

      <GridItem>
        <FilterSelect<AudienceType>
          id="resource-audience-filter"
          value={audience}
          onChange={(value) => setAudience(value)}
          options={uniqueAudiences}
          label="Filter by audience"
          placeholder="Audience: All"
        />
      </GridItem>

      <GridItem>
        <FilterSelect<FocusType>
          id="resource-focus-filter"
          value={focus}
          onChange={(value) => setFocus(value)}
          options={uniqueFocuses}
          label="Filter by focus"
          placeholder="Focus: All"
        />
      </GridItem>

      <GridItem>
        <Box width="100%">
          <VisuallyHidden>
            <label htmlFor="resource-sort-mobile">Sort resources</label>
          </VisuallyHidden>
          <Select
            id="resource-sort-mobile"
            value={sort}
            onChange={(e) => setSort(e.target.value as SortType)}
            aria-label="Sort resources"
            width="100%"
            borderColor="#F2F2ED"
            fontSize="14px"
            color="#545454"
            fontWeight="normal"
            height="44px"
            _focus={{
              borderColor: '#007377',
              boxShadow: '0 0 0 1px #007377',
            }}
          >
            <option value="Latest">Sort: Latest</option>
            <option value="Oldest">Oldest</option>
            <option value="A-Z">A-Z</option>
            <option value="Z-A">Z-A</option>
          </Select>
        </Box>
      </GridItem>
    </Grid>

    {/* Desktop view: Flex layout */}
    <Flex
      display={['none', 'none', 'flex']}
      flexDirection="row"
      gap={4}
      width="100%"
      flexWrap="nowrap"
      justifyContent="flex-start"
      alignItems="center"
    >
      <Flex gap={4} flexWrap="nowrap">
        <Box width="160px" flexShrink={0}>
          <FilterSelect<ResourceType>
            id="resource-type-filter-desktop"
            value={type}
            onChange={(value) => setType(value)}
            options={uniqueTypes}
            label="Filter by type"
            placeholder="Category: All"
            width="100%"
          />
        </Box>

        <Box width="160px" flexShrink={0}>
          <FilterSelect<AudienceType>
            id="resource-audience-filter-desktop"
            value={audience}
            onChange={(value) => setAudience(value)}
            options={uniqueAudiences}
            label="Filter by audience"
            placeholder="Audience: All"
            width="100%"
          />
        </Box>

        <Box width="160px" flexShrink={0}>
          <FilterSelect<FocusType>
            id="resource-focus-filter-desktop"
            value={focus}
            onChange={(value) => setFocus(value)}
            options={uniqueFocuses}
            label="Filter by focus"
            placeholder="Focus: All"
            width="100%"
          />
        </Box>
      </Flex>

      <Box width="160px" flexShrink={0} ml="auto">
        <VisuallyHidden>
          <label htmlFor="resource-sort-desktop">Sort resources</label>
        </VisuallyHidden>
        <Select
          id="resource-sort-desktop"
          value={sort}
          onChange={(e) => setSort(e.target.value as SortType)}
          aria-label="Sort resources"
          width="100%"
          borderColor="#F2F2ED"
          fontSize="16px"
          color="#545454"
          fontWeight="normal"
          height="48px"
          _focus={{
            borderColor: '#007377',
            boxShadow: '0 0 0 1px #007377',
          }}
        >
          <option value="Latest">Sort: Latest</option>
          <option value="Oldest">Oldest</option>
          <option value="A-Z">A-Z</option>
          <option value="Z-A">Z-A</option>
        </Select>
      </Box>
    </Flex>
  </Flex>
);

export default ResourcesFilterBar;
